import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import PlanDeComDossierSwitcher from '../../components/kkp/PlanDeComDossierSwitcher';
import ChiffrageEntrepriseAutocomplete from '../../components/iti/chiffrage/ChiffrageEntrepriseAutocomplete';
import ChiffrageWipForm from '../../components/iti/chiffrage/ChiffrageWipForm';
import ChiffrageInvestissementPreview from '../../components/iti/chiffrage/ChiffrageInvestissementPreview';
import ChiffrageAssistant from '../../components/iti/chiffrage/ChiffrageAssistant';
import { AppBadge, AppButton, AppInput, AppSelect } from '../../components/ui';
import chiffrageSiteService from '../../services/chiffrageSiteService';
import kkpService from '../../services/kkpService';
import useChiffrageSiteSession from '../../hooks/useChiffrageSiteSession';
import useChiffrageDossierActions from '../../hooks/useChiffrageDossierActions';
import { notify } from '../../components/common/AppToast';
import {
  clampWizardStep,
  clearChiffrageSiteDraft,
  mergeWipWithDefaults,
  resolveCommercialId,
} from '../../utils/chiffrageSiteDraftStorage';
import { matchCommercialOption } from '../../utils/matchCommercialOption';
import authService from '../../services/authService';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/sites-web.scss';
import '../../styles/chiffrage-site.scss';
import '../../styles/chiffrage-site-layout.scss';

const CALC_DEBOUNCE_MS = 450;
const SAVE_DEBOUNCE_MS = 250;

const ChiffrageSitePage = () => {
  const { ask, modal } = useAppConfirm();
  const {
    data,
    setData,
    dossiers,
    loading: loadingSession,
    saving,
    dossierId,
    save,
    reset,
    startNew,
    openDossier,
    duplicateDossier,
    deleteDossier,
    renameDossier,
    loadDossiers,
  } = useChiffrageSiteSession();

  const [meta, setMeta] = useState(null);
  const [wip, setWip] = useState({});
  const [variables, setVariables] = useState({});
  const [titre, setTitre] = useState('Chiffrage site internet');
  const [entreprise, setEntreprise] = useState('');
  const [dolibarrThirdpartyId, setDolibarrThirdpartyId] = useState(null);
  const [client, setClient] = useState('');
  const [resolvingClient, setResolvingClient] = useState(false);
  const [commercialId, setCommercialId] = useState([]);
  const [affichagePrix, setAffichagePrix] = useState('Oui');
  const [wizardStep, setWizardStep] = useState(0);
  const [result, setResult] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pushingDolibarr, setPushingDolibarr] = useState(false);
  const [hydratedId, setHydratedId] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const calcRequestId = useRef(0);
  const saveTimerRef = useRef(null);
  const clientLookupAbortRef = useRef(null);
  const skipNextSaveRef = useRef(false);

  const commercialIdValue = commercialId[0] || '';
  const loading = loadingMeta || loadingSession;

  const commercialOptions = useMemo(
    () => (meta?.commerciaux ?? []).map((c) => ({ value: String(c.id), label: c.nom })),
    [meta?.commerciaux],
  );

  const applyDossier = useCallback((full, { commerciaux = [], defaultWip = {} } = {}) => {
    const dossier = full?.dossier ?? {};
    skipNextSaveRef.current = true;
    setHydratedId(dossier.id != null ? String(dossier.id) : null);
    setWip(mergeWipWithDefaults(defaultWip, full?.wip));
    setTitre(dossier.titre ?? 'Chiffrage site internet');
    setEntreprise(dossier.entreprise_label ?? '');
    setDolibarrThirdpartyId(dossier.dolibarr_thirdparty_id ?? null);
    setClient(dossier.client_label ?? '');
    setCommercialId(() => {
      const resolved = resolveCommercialId(dossier.commercial_id, commerciaux);
      return resolved ? [String(resolved)] : [];
    });
    setAffichagePrix(dossier.affichage_prix === 'Non' ? 'Non' : 'Oui');
    setWizardStep(clampWizardStep(dossier.wizard_step));
    setResult(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const metaData = await chiffrageSiteService.getMeta();
        if (cancelled) return;
        setMeta(metaData);
        setVariables(metaData.variables ?? {});
        clearChiffrageSiteDraft();
      } catch (err) {
        if (!cancelled) notify.error(err.message);
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!meta || !data?.dossier?.id) return;
    if (hydratedId === String(data.dossier.id)) return;
    applyDossier(data, {
      commerciaux: meta.commerciaux,
      defaultWip: meta.default_wip,
    });
  }, [meta, data, hydratedId, applyDossier]);

  const flushSave = useCallback(async (payload) => {
    if (!dossierId) return null;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    return save(payload);
  }, [dossierId, save]);

  const scheduleSave = useCallback((payload) => {
    if (!dossierId || skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      save(payload).catch(() => {
        notify.error('Impossible d’enregistrer le chiffrage.');
      });
    }, SAVE_DEBOUNCE_MS);
  }, [dossierId, save]);

  useEffect(() => {
    if (!dossierId || hydratedId !== String(dossierId)) return undefined;
    scheduleSave({
      titre,
      entreprise_label: entreprise,
      dolibarr_thirdparty_id: dolibarrThirdpartyId,
      client_label: client,
      commercial_id: commercialIdValue || null,
      affichage_prix: affichagePrix,
      wizard_step: wizardStep,
      wip,
    });
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [
    dossierId,
    hydratedId,
    titre,
    entreprise,
    dolibarrThirdpartyId,
    client,
    commercialIdValue,
    affichagePrix,
    wizardStep,
    wip,
    scheduleSave,
  ]);

  const fillClientFromDolibarr = useCallback(async (thirdpartyId) => {
    if (clientLookupAbortRef.current) {
      clientLookupAbortRef.current.abort();
    }

    const id = Number(thirdpartyId) || 0;
    if (id <= 0) {
      setResolvingClient(false);
      return;
    }

    const controller = new AbortController();
    clientLookupAbortRef.current = controller;
    setResolvingClient(true);
    try {
      const detail = await kkpService.getDolibarrClient(id);
      if (controller.signal.aborted) return;
      const clientLabel = String(detail?.client_label ?? '').trim();
      if (clientLabel) {
        setClient(clientLabel);
      }
      const matchedCommercial = matchCommercialOption(
        (meta?.commerciaux ?? []).map((c) => ({
          value: String(c.id),
          label: c.nom,
          email: c.email,
        })),
        {
          commercial_id: detail?.commercial_id,
          commercial_nom: detail?.commercial_nom,
          commercial_email: detail?.commercial_email,
          commercial_emails: detail?.commercial_emails,
        },
      );
      if (matchedCommercial) {
        setCommercialId([matchedCommercial.value]);
      }
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
    } finally {
      if (!controller.signal.aborted) {
        setResolvingClient(false);
      }
    }
  }, [meta?.commerciaux]);

  useEffect(() => () => {
    clientLookupAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (loading || !meta) return undefined;

    const requestId = ++calcRequestId.current;
    const timer = setTimeout(async () => {
      setCalculating(true);
      try {
        const calc = await chiffrageSiteService.calculate({
          wip,
          variables,
          affichage_prix: affichagePrix,
        });
        if (requestId === calcRequestId.current) {
          setResult(calc);
        }
      } catch (err) {
        if (requestId === calcRequestId.current) {
          notify.error(err.message);
        }
      } finally {
        if (requestId === calcRequestId.current) {
          setCalculating(false);
        }
      }
    }, CALC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [wip, variables, affichagePrix, loading, meta]);

  const handleGenerateSlides = async () => {
    if (!result || !meta?.can_generate_slides) return;
    setGenerating(true);
    try {
      await notify.run(
        () => chiffrageSiteService.generateSlides({
          wip,
          variables,
          titre,
          entreprise,
          client,
          commercial_id: commercialIdValue,
          affichage_prix: affichagePrix,
        }),
        {
          loading: 'Génération Google Slides en cours…',
          success: (res) => {
            if (res.presentation_url) {
              window.open(res.presentation_url, '_blank', 'noopener,noreferrer');
            }
            return 'Présentation générée';
          },
        },
      );
    } catch {
      // notify.run affiche déjà le toast d'erreur
    } finally {
      setGenerating(false);
    }
  };

  const handlePushDolibarr = async () => {
    if (!result || !meta?.can_push_dolibarr) return;
    if (!dolibarrThirdpartyId) {
      notify.warning('Sélectionnez un client dans la liste Dolibarr (champ Entreprise).');
      return;
    }
    setPushingDolibarr(true);
    try {
      const commercial = (meta?.commerciaux ?? []).find((c) => String(c.id) === String(commercialIdValue));
      await notify.run(
        () => chiffrageSiteService.pushDolibarr({
          wip,
          variables,
          titre,
          entreprise,
          dolibarr_thirdparty_id: dolibarrThirdpartyId,
          client,
          commercial_id: commercialIdValue,
          commercial,
          affichage_prix: affichagePrix,
        }),
        {
          loading: 'Création de la propal Dolibarr en cours…',
          success: (res) => {
            if (res.propal_url) {
              window.open(res.propal_url, '_blank', 'noopener,noreferrer');
            }
            return `Propal ${res.propal_ref || '#' + res.propal_id} créée pour ${res.client_name}`;
          },
        },
      );
    } catch {
      // notify.run affiche déjà le toast d'erreur
    } finally {
      setPushingDolibarr(false);
    }
  };

  const handleCopyDolibarr = async () => {
    if (!result?.phases) return;
    const text = result.phases
      .map((phase) => `${phase.title}\n\n${phase.description}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      notify.success('Texte copié. Collez-le dans votre fiche devis Dolibarr.');
    } catch {
      notify.error('Impossible de copier dans le presse-papier.');
    }
  };

  const handleResetDraft = async () => {
    if (!dossierId || saving) return;
    const ok = await ask({
      title: 'Réinitialiser ce chiffrage ?',
      message: 'Les paramètres du formulaire seront remis à zéro.',
      confirmLabel: 'Réinitialiser',
      confirmVariant: 'danger',
    });
    if (!ok) {
      return;
    }
    clientLookupAbortRef.current?.abort();
    try {
      const full = await reset();
      if (full) {
        setData(full);
        applyDossier(full, {
          commerciaux: meta?.commerciaux,
          defaultWip: meta?.default_wip,
        });
        notify.success('Chiffrage réinitialisé.');
      }
    } catch {
      notify.error('Impossible de réinitialiser ce chiffrage.');
    }
  };

  const {
    handleOpenDossier,
    handleNewDossier,
    handleDuplicateDossier,
    handleDeleteDossier,
    handleRenameDossier,
  } = useChiffrageDossierActions({
    session: {
      dossierId,
      saving,
      openDossier,
      startNew,
      duplicateDossier,
      deleteDossier,
      renameDossier,
    },
    ask,
    saveTimerRef,
    flushBeforeSwitch: () => flushSave({
      titre,
      entreprise_label: entreprise,
      dolibarr_thirdparty_id: dolibarrThirdpartyId,
      client_label: client,
      commercial_id: commercialIdValue || null,
      affichage_prix: affichagePrix,
      wizard_step: wizardStep,
      wip,
    }),
    onSessionApplied: (full, ctx) => {
      if (ctx?.action === 'rename') {
        setData(full);
        setTitre(full.dossier?.titre ?? ctx.titre ?? 'Chiffrage site internet');
        return;
      }
      setData(full);
      applyDossier(full, {
        commerciaux: meta?.commerciaux,
        defaultWip: meta?.default_wip,
      });
    },
  });

  const showPrices = affichagePrix === 'Oui';
  const busy = saving || generating || pushingDolibarr;
  const collaborationRequired = result?.collaboration?.required === true;
  const mineCount = (dossiers ?? []).length;

  const handleApplyPatch = (patch) => {
    if (!patch || typeof patch !== 'object') return;
    setWip((current) => ({ ...current, ...patch }));
  };

  const handleToggleStatut = async (dossier) => {
    if (!dossier?.id || busy) return;
    try {
      const full = await chiffrageSiteService.updateDossier(dossier.id, {
        statut: dossier.validated ? 'brouillon' : 'valide',
      });
      if (String(dossier.id) === String(dossierId)) {
        setData(full);
      }
      await loadDossiers();
    } catch (err) {
      notify.error(err.message || 'Impossible de changer le statut.');
    }
  };

  return (
    <MasterLayout>
      <div className="sites-web chiffrage-site">
        <header className="sw-head">
          <div className="sw-head__titles">
            <h1 className="fw-semibold mb-0 app-page-title">Chiffrage site</h1>
            <p>Répondez aux questions étape par étape : le devis se calcule à chaque réponse.</p>
          </div>
          <div className="chiffrage-site__actions">
            <span className="chiffrage-save">
              <Icon icon={saving ? 'lucide:loader-circle' : 'lucide:cloud-check'} width={16} />
              {saving ? 'Enregistrement…' : 'Enregistré automatiquement'}
            </span>
            <AppButton
              variant={assistantOpen ? 'warning' : 'outline-secondary'}
              icon="lucide:sparkles"
              onClick={() => setAssistantOpen((open) => !open)}
            >
              Assistant
            </AppButton>
            <AppButton variant="outline-secondary" icon="lucide:folder-open" onClick={() => setListOpen(true)}>
              Mes chiffrages ({mineCount})
            </AppButton>
            <AppButton variant="outline-secondary" icon="lucide:plus" disabled={busy} onClick={handleNewDossier}>
              Nouveau
            </AppButton>
          </div>
        </header>

        {loading && !data && <KkpPlanningEmptyState loading />}

        {data && (
          <>
            <section className="chiffrage-project">
              <div className="chiffrage-project__label">
                Le projet
                <AppBadge variant={data.dossier?.validated ? 'success' : 'warning'} pill>
                  {data.dossier?.validated ? 'Validé' : 'En cours'}
                </AppBadge>
              </div>
              <div className="chiffrage-project__grid">
                <label className="chiffrage-field" htmlFor="chiffrage-titre">
                  Titre
                  <AppInput id="chiffrage-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Titre" />
                </label>
                <label className="chiffrage-field">
                  Entreprise
                  <ChiffrageEntrepriseAutocomplete
                    value={entreprise}
                    dolibarrConfigured={meta?.dolibarr_configured !== false}
                    onSelect={({ id, nom }) => {
                      const nextId = id ?? null;
                      setEntreprise(nom ?? '');
                      setDolibarrThirdpartyId(nextId);
                      if (nextId) fillClientFromDolibarr(nextId);
                    }}
                  />
                </label>
                <label className="chiffrage-field" htmlFor="chiffrage-client">
                  Contact
                  <AppInput
                    id="chiffrage-client"
                    value={client}
                    onChange={(e) => setClient(e.target.value)}
                    placeholder={resolvingClient ? 'Recherche contact…' : 'Nom et prénom'}
                    disabled={resolvingClient}
                    autoComplete="off"
                  />
                </label>
                <label className="chiffrage-field" htmlFor="chiffrage-commercial">
                  Commercial
                  <AppSelect
                    id="chiffrage-commercial"
                    value={commercialIdValue}
                    onChange={(e) => setCommercialId(e.target.value ? [e.target.value] : [])}
                  >
                    <option value="">Choisir</option>
                    {commercialOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </AppSelect>
                </label>
              </div>
            </section>

            <div className="chiffrage-layout">
              {loadingMeta ? (
                <KkpPlanningEmptyState loading />
              ) : (
                <ChiffrageWipForm
                  meta={meta}
                  wip={wip}
                  onChange={setWip}
                  wizardStep={wizardStep}
                  onWizardStepChange={setWizardStep}
                  disabled={busy}
                />
              )}
              {assistantOpen ? (
                <ChiffrageAssistant
                  result={result}
                  wip={wip}
                  variables={variables}
                  calculating={calculating}
                  wizardStep={wizardStep}
                  dossierId={dossierId}
                  onApplyPatch={handleApplyPatch}
                  onClose={() => setAssistantOpen(false)}
                />
              ) : (
                <ChiffrageInvestissementPreview
                  result={result}
                  affichagePrix={showPrices}
                  onAffichagePrixChange={(show) => setAffichagePrix(show ? 'Oui' : 'Non')}
                  loading={calculating && !result}
                  onCopyDolibarr={result && !collaborationRequired ? handleCopyDolibarr : null}
                  onGenerateSlides={handleGenerateSlides}
                  onPushDolibarr={handlePushDolibarr}
                  onReset={handleResetDraft}
                  canGenerateSlides={!!meta?.can_generate_slides}
                  canPushDolibarr={!!meta?.can_push_dolibarr}
                  generating={generating}
                  pushing={pushingDolibarr}
                  busy={busy}
                />
              )}
            </div>

            <PlanDeComDossierSwitcher
              hideBar
              open={listOpen}
              onOpenChange={setListOpen}
              appearance="sites"
              dossiers={dossiers}
              current={data.dossier}
              currentId={dossierId}
              disabled={busy}
              saving={saving}
              canSeeTeamDossiers={authService.isAdminCommercial() || authService.isSuperAdmin()}
              commerciaux={meta?.commerciaux ?? []}
              onRefreshDossiers={loadDossiers}
              onOpen={handleOpenDossier}
              onNew={handleNewDossier}
              onDuplicate={handleDuplicateDossier}
              onDelete={handleDeleteDossier}
              onRename={handleRenameDossier}
              onToggleStatut={handleToggleStatut}
            />
          </>
        )}

        {result && !collaborationRequired ? (
          <div className="chiffrage-mobile-bar">
            <span>
              <small>Création du site</small>
              <strong>{Number(result.total_price).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</strong>
            </span>
            <span>+ {Number(result.budget_n2).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € / an</span>
          </div>
        ) : null}
      </div>
      {modal}
    </MasterLayout>
  );
};

export default ChiffrageSitePage;
